import React, { useState } from 'lib/react';
import {
  EVGrid, EVTypography, EVInputLabel, EVMenuItem, EVSelect,
} from 'lib/components';
import TextInputField from 'commonComponents/textInputField';
import PropTypes from 'prop-types';
import {
  US_STATES, CANADA_PROVINCES, ZIP_CODE_US_REGEX, ZIP_CODE_CA_REGEX,
} from 'constants/AppConstants';
import clsx from 'clsx';
import { useTranslation } from 'lib/translation';
import { getPropertyValues } from 'features/orderPreferences/OrderPreferences.utils';
import CustomCoverStyles from '../CustomCoverStyles';

function CompanyInformation({ orderPreferences, inputFieldValues, handlePropertyChange }) {
  const classes = CustomCoverStyles();
  const { t } = useTranslation();
  const statesList = [...US_STATES, ...CANADA_PROVINCES];

  const [zipError, setZipError] = useState(false);
  const handleChange = ({ target: { id, value } }) => {
    if (id === 'reportOptionsCoverCompanyZip') {
      const isValidZip = ZIP_CODE_US_REGEX.test(value) || ZIP_CODE_CA_REGEX.test(value);
      setZipError(!isValidZip);
    }
    handlePropertyChange(id, 'value', value);
  };

  const isAdmin = orderPreferences?.isAdmin;

  const handleSelectOptionChange = ({ target: { value } }) => handlePropertyChange('reportOptionsCoverCompanyState', 'value', value);

  return (
    <EVGrid className={classes.coverPageInformationContainer}>
      <EVTypography className={classes.coverPageInformationTextStyles}>
        {t('order_preferences__company_information')}
      </EVTypography>
      <EVGrid
        container
        direction="column"
        style={{ display: 'flex', gap: '24px' }}
        component="form"
      >
        <EVGrid className={classes.coverPageTitleSubtitleContainer}>
          <EVGrid className={classes.inputFieldContainer}>
            <TextInputField
              handleChange={handleChange}
              inputId="reportOptionsCoverTitle"
              inputValue={getPropertyValues(orderPreferences, inputFieldValues, 'reportOptionsCoverTitle')?.value}
              inputLabelProps={{ className: classes.coverPageInputLabels }}
              labelText={t('order_preferences__custom_cover_company_report_title')}
              inputFieldProps={{
                disabled: !isAdmin && !orderPreferences?.reportOptionsCoverTitle?.canEdit,
                InputProps: { classes: { root: classes.inputTextField } },
              }}
            />
          </EVGrid>
          <EVGrid className={classes.inputFieldContainer}>
            <TextInputField
              handleChange={handleChange}
              inputId="reportOptionsCoverSubTitle"
              inputValue={getPropertyValues(orderPreferences, inputFieldValues, 'reportOptionsCoverSubTitle').value}
              inputLabelProps={{ className: classes.coverPageInputLabels }}
              labelText={t('order_preferences__custom_cover_company_report_subtitle')}
              inputFieldProps={{
                disabled: !isAdmin && !orderPreferences?.reportOptionsCoverSubTitle?.canEdit,
                InputProps: { classes: { root: classes.inputTextField } },
              }}
            />
          </EVGrid>
        </EVGrid>
        <EVGrid className={classes.inputFieldContainer}>
          <TextInputField
            handleChange={handleChange}
            inputId="reportOptionsCoverCompanyName"
            inputValue={getPropertyValues(orderPreferences, inputFieldValues, 'reportOptionsCoverCompanyName')?.value}
            inputLabelProps={{ className: classes.coverPageInputLabels }}
            labelText={t('order_preferences__custom_cover_company_company_name')}
            inputFieldProps={{
              disabled: !isAdmin && !orderPreferences?.reportOptionsCoverCompanyName?.canEdit,
              InputProps: { classes: { root: classes.inputTextField } },
            }}
          />
        </EVGrid>
        <EVGrid className={classes.inputFieldContainer}>
          <TextInputField
            handleChange={handleChange}
            inputId="reportOptionsCoverCompanyStreet"
            inputValue={getPropertyValues(orderPreferences, inputFieldValues, 'reportOptionsCoverCompanyStreet')?.value}
            inputLabelProps={{ className: classes.coverPageInputLabels }}
            labelText={t('order_preferences__custom_cover_company_company_address')}
            inputFieldProps={{
              disabled: !isAdmin && !orderPreferences?.reportOptionsCoverCompanyStreet?.canEdit,
              InputProps: { classes: { root: classes.inputTextField } },
            }}
          />
        </EVGrid>
        <EVGrid container wrap="nowrap" style={{ display: 'flex', gap: '16px' }}>
          <EVGrid style={{
            display: 'flex', flexDirection: 'column', gap: '10px', flex: '1 1 auto',
          }}
          >
            <TextInputField
              handleChange={handleChange}
              inputId="reportOptionsCoverCompanyCity"
              inputValue={getPropertyValues(orderPreferences, inputFieldValues, 'reportOptionsCoverCompanyCity')?.value}
              inputLabelProps={{ className: classes.coverPageInputLabels }}
              labelText={t('order_preferences__custom_cover_company_city')}
              inputFieldProps={{
                disabled: !isAdmin && !orderPreferences?.reportOptionsCoverCompanyCity?.canEdit,
                InputProps: { classes: { root: classes.inputTextField } },
              }}
            />
          </EVGrid>
          <EVGrid className={classes.inputStateContainer}>
            <EVInputLabel id="state" className={classes.coverPageInputLabels}>
              {t('order_preferences__custom_cover_company_state')}
            </EVInputLabel>
            <EVSelect
              variant="outlined"
              id="reportOptionsCoverCompanyState"
              disabled={isAdmin && !orderPreferences?.reportOptionsCoverCompanyState?.canEdit}
              value={getPropertyValues(orderPreferences, inputFieldValues, 'reportOptionsCoverCompanyState')?.value}
              onChange={handleSelectOptionChange}
              inputProps={{ classes: { root: clsx(classes.inputTextField, 'select') } }}
            >
              {statesList.map(({ abbreviation }) => (
                <EVMenuItem value={abbreviation} key={abbreviation}>
                  <em style={{ fontStyle: 'normal' }}>{abbreviation}</em>
                </EVMenuItem>
              ))}
            </EVSelect>
          </EVGrid>
          <EVGrid className={classes.inputStateContainer}>
            <TextInputField
              handleChange={handleChange}
              inputId="reportOptionsCoverCompanyZip"
              inputValue={getPropertyValues(orderPreferences, inputFieldValues, 'reportOptionsCoverCompanyZip')?.value}
              inputLabelProps={{ className: classes.coverPageInputLabels }}
              labelText={t('order_preferences__custom_cover_company_zipcode')}
              inputFieldProps={{
                inputProps: { maxLength: 10 },
                disabled: !isAdmin && !orderPreferences?.reportOptionsCoverCompanyZip?.canEdit,
                InputProps: { classes: { root: classes.inputTextField } },
              }}
              helperText={zipError && t('order__valid_zip_code')}
            />
          </EVGrid>
        </EVGrid>
      </EVGrid>
    </EVGrid>
  );
}

CompanyInformation.propTypes = {
  handlePropertyChange: PropTypes.func.isRequired,
  orderPreferences: PropTypes.shape().isRequired,
  inputFieldValues: PropTypes.shape().isRequired,
};
export default CompanyInformation;
